import { MediaMatcher } from '@angular/cdk/layout';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ApiService } from 'src/app/services/api.service';
import { NotifyService } from 'src/app/services/notify.service';
import { ValueService } from 'src/app/services/value.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-infrastructure',
  templateUrl: './infrastructure.component.html',
  styleUrls: ['./infrastructure.component.css'],
})
export class InfrastructureComponent implements OnInit {
  hrForm: FormGroup;
  private saveApi = environment.url + 'preincubation/infrastructure/save';
  private getApi = environment.url + 'preincubation/infrastructure/get?userId=';
  private deleteFacilityApi =
    environment.url + 'preincubation/facility/Remove?id=';

  /**
   * Initializes the InfrastructureComponent with required services and sets up the form and mobile query listener.
   *
   * @param changeDetectorRef Reference to Angular's change detector for triggering change detection.
   * @param media Service to match media queries for responsive design.
   * @param fb FormBuilder service for creating reactive forms.
   * @param makeapi Service for making API calls.
   * @param getdata Service to retrieve values or data needed by the component.
   * @param notify Service for displaying notifications.
   * @param dialog Material dialog service for opening dialogs.
   */
  constructor(
    changeDetectorRef: ChangeDetectorRef,
    media: MediaMatcher,
    private fb: FormBuilder,
    private makeapi: ApiService,
    private getdata: ValueService,
    private notify: NotifyService,
    public dialog: MatDialog
  ) {
    /**
     * Create the form group with the required fields for the infrastructure details.
     */
    this.hrForm = this.fb.group({
      id: [''],
      infraId: ['0'],
      applicationId: ['0'],
      incubationspace: ['', Validators.required],
      coworkingspace: ['', Validators.required],
      meetingroom: ['', Validators.required],
      makerspace: ['', Validators.required],
      facility: this.fb.array([]),
    });
    /**
     * Set up the mobile query listener to detect changes in the mobile query.
     */
    this.mobileQuery = media.matchMedia('(max-width: 992px)');
    this._mobileQueryListener = () => changeDetectorRef.detectChanges();
  }
  mobileQuery: MediaQueryList;
  private _mobileQueryListener: () => void;
  ngAfterViewInit(): void {
    this.mobileQuery.removeListener(this._mobileQueryListener);
  }
  get setting() {
    return this.hrForm.controls;
  }

  get facility() {
    return this.hrForm.get('facility') as FormArray;
  }
  roleName: any = '';
  /**
   * Angular lifecycle hook that is called after data-bound properties are initialized.
   * Retrieves the user's role from session storage and fetches data from the API.
   */
  ngOnInit(): void {
    // Get the user's role from session storage
    this.roleName = sessionStorage.getItem('role');

    // Fetch data from the API
    this.getDataApi();
  }
  getValue: any = {};
  showForm: boolean = true;
  /**
   * Fetches data from the API and populates the form with the data.
   * If the application is not editable, the form is disabled.
   * If the API response is successful, the form is populated and the add row button is conditionally shown.
   */
  getDataApi() {
    this.facility.clear();
    this.getdata.amizhthLoader('show');
    this.makeapi
      .method(this.getApi + sessionStorage.getItem('userId'), '', 'get')
      .subscribe((data) => {
        this.getdata.amizhthLoader('hide');
        this.getValue = data;
        if (data.edit === false) {
          this.showForm = true;
        } else {
          this.showForm = false;
        }
        if (data.responseStatus != 'Failed') {
          // Patch the form with the API response

          // If the API response contains facility data, loop through it and add rows to the form array
          if (data.facility && data.facility.length > 0) {
            for (let i = 0; i < data.facility.length; i++) {
              if (data.facility[i].availability == true) {
                data.facility[i].availability = 'true';
              } else {
                data.facility[i].availability = 'false';
              }
              // Add a row to the form array for each facility
              this.addRow();
            }
          }
          this.hrForm.patchValue(data);
          // If the API response does not contain facility data, add a row to the form array
          if (!data.facility || data.facility.length == 0) {
            this.addRow();
          }
        }
      });
  }
  userId = sessionStorage.getItem('userId');
  /**
   * Saves the form data to the API.
   * If the form is invalid, the form's error messages are shown.
   * If the API response is successful, the form is cleared and the step is incremented.
   */
  saveBtn() {
    const getform = this.hrForm.value;
    if (this.hrForm.invalid) {
      // If the form is invalid, mark all the fields as touched
      this.hrForm.markAllAsTouched();
      this.notify.notifyError('Please fill all the mandatory fields !!');
    } else {
      // Show the loading indicator
      this.getdata.amizhthLoader('show');
      // Add the user ID to the form data
      getform.userid = this.userId;
      // Post the form data to the API
      this.makeapi.method(this.saveApi, getform, 'post').subscribe((data) => {
        // Hide the loading indicator
        this.getdata.amizhthLoader('hide');
        if (data.responseStatus == 'Success') {
          // If the API response is successful, increment the step
          this.step++;
          // Show a success message
          this.notify.notifysuccess(data.responseMessage);
          // Refetch the data to update the form
          this.getDataApi();
        } else {
          // If the API response is not successful, show an error message
          this.notify.notifyError(data.responseMessage);
        }
      });
    }
  }
  step = 0;
  setStep(index: number) {
    this.step = index;
  }
  /**
   * Called when the user clicks on the "Next" button.
   * If the user is on step 0, adds validators to the form controls.
   * If the user is on step 1, adds validators to the form controls in the facility array.
   * If the user is on step 2, clears the validators from the form controls.
   * Then, calls the saveBtn method to save the form data.
   */
  nextStep() {
    if (this.step == 0) {
      // Add validators to the form controls if the user is on step 0
      this.hrForm.controls['incubationspace'].setValidators([
        Validators.required,
      ]);
      this.hrForm.controls['coworkingspace'].setValidators([
        Validators.required,
      ]);
      this.hrForm.controls['meetingroom'].setValidators([Validators.required]);
      this.hrForm.controls['makerspace'].setValidators([Validators.required]);
    } else if (this.step == 1) {
      // Add validators to the form controls in the facility array if the user is on step 1
      for (let i = 0; i < this.facility.length; i++) {
        const item = this.facility.at(i);
        item.get('facilityname').setValidators([Validators.required]);
        item.get('space').setValidators([Validators.required]);
        item.get('availableinstruments').setValidators([Validators.required]);

        item.get('facilityname').updateValueAndValidity();
        item.get('space').updateValueAndValidity();
        item.get('availableinstruments').updateValueAndValidity();
      }
    } else {
      // Clear the validators from the form controls if the user is on step 2
      if (this.facility.length > 0) {
        for (let i = 0; i < this.facility.length; i++) {
          const item = this.facility.at(i);
          item.get('facilityname').clearValidators();
          item.get('space').clearValidators();
          item.get('availableinstruments').clearValidators();
          item.get('facilityname').updateValueAndValidity();
          item.get('space').updateValueAndValidity();
          item.get('availableinstruments').updateValueAndValidity();
        }
      }
    }

    // Update the validity of the form controls
    this.hrForm.controls['incubationspace'].updateValueAndValidity();
    this.hrForm.controls['coworkingspace'].updateValueAndValidity();
    this.hrForm.controls['meetingroom'].updateValueAndValidity();
    this.hrForm.controls['makerspace'].updateValueAndValidity();

    // Call the saveBtn method to save the form data
    this.saveBtn();
  }
  prevStep() {
    this.step--;
  }

  /**
   * Adds a new row to the facility array.
   * The row is represented by a form group with the following controls:
   * - id: a unique identifier for the row.
   * - preincinfrastructureid: the id of the pre incubation infrastructure.
   * - facilityname: the name of the facility.
   * - space: the amount of space available in the facility.
   * - availableinstruments: the number of instruments available in the facility.
   */
  addRow() {
    const facilityForm = this.fb.group({
      /**
       * Unique identifier for the row.
       */
      id: 0,
      /**
       * The id of the pre incubation infrastructure.
       */
      preincinfrastructureid: 0,
      /**
       * The name of the facility.
       */
      facilityname: ['', Validators.required],
      /**
       * The amount of space available in the facility.
       */
      space: ['', Validators.required],
      /**
       * The number of instruments available in the facility.
       */
      availableinstruments: ['', Validators.required],
    });
    /**
     * Adds the newly created form group to the facility array.
     */
    this.facility.push(facilityForm);
  }

  /**
   * Recursively marks all controls in the given form group as touched.
   * This is used to show all error messages at once.
   * @param formGroup The form group to mark all controls as touched.
   */
  markAllAsTouched(formGroup: FormGroup): void {
    // Iterate over all controls in the form group
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      if (control instanceof FormArray) {
        // If the control is a form array, iterate over all its controls
        control.controls.forEach((group: FormGroup) =>
          this.markAllAsTouched(group)
        );
      } else {
        // If the control is not a form array, mark it as touched
        control?.markAsTouched();
      }
    });
  }
  /**
   * Deletes a row from the facility array.
   * @param index The index of the row to delete.
   * @param id The id of the row to delete.
   */
  deleteRow(index: number, id: number): void {
    if (id != 0) {
      // Show the loader
      this.getdata.amizhthLoader('show');

      // Make the API call to delete the facility
      this.makeapi
        .method(this.deleteFacilityApi + id, '', 'delete')
        .subscribe((data) => {
          // Hide the loader
          this.getdata.amizhthLoader('hide');

          if (data.responseStatus != 'Failed') {
            // If the index is 0 and the id is not 0, remove the row with the given id from the array
            if (index == 0 && id != 0) {
              this.facility.removeAt(id);
            }

            // Remove the row from the array
            if (id != 0) {
              this.facility.removeAt(index);
            }
          }
        });
    } else if (index == 0 && id == 0) {
      // Remove the row from the array if the index is 0 and the id is 0
      this.facility.removeAt(index);
    } else if (index == 0 && id != 0) {
      // Remove the row from the array if the index is 0 and the id is not 0
      this.facility.removeAt(index);
    } else {
      // Remove the row from the array if the index is not 0
      this.facility.removeAt(index);
      // If the index is 0, add a new row to the array
      if (index == 0) {
        this.addRow();
      }
    }
  }
}
